import {
  AUDIENCE_FIELD_DEFINITIONS,
  AUDIENCE_FIELD_KEYS,
  BRAND_FIELD_DEFINITIONS,
  BRAND_FIELD_KEYS,
  BRAND_LIMITS,
  BRAND_WIZARD_STEP_COUNT,
  BRAND_WIZARD_STEPS,
  canonicalSources,
  normalizeBrandText,
  type AudienceFieldKey,
  type BrandAudienceInput,
  type BrandAudienceView,
  type BrandDraftValue,
  type BrandFieldInput,
  type BrandFieldKey,
  type BrandFieldKind,
  type BrandFieldsInput,
  type BrandImportDraft,
  type BrandImportJobView,
  type BrandOnboardingStartMode,
  type BrandProfileView,
  type BrandSourceTag,
  type BrandWizardStep,
  type FieldProvenanceView,
  type OnboardingStepSaveRequest,
  type ValuePhilosophy,
} from '@mg/shared';
import { textLimitError } from '../../brand/fields/fieldParts';

/**
 * Brand Setup's draft: every field's value as the wizard shows it, next to its saved value and where
 * each came from, as one pure reducer (web.md §5.3, final-design §2.14). The page holds it with
 * useReducer; nothing here talks to the server.
 *
 * The rule it exists for: an import never overwrites what the person has, and nothing is saved until a
 * step is. An import's values are a standing OFFER: they fill a field only while it is empty, untouched
 * this session and not decided (saved, even empty) since the import run began. Untouched fills are not
 * "user changes", so leaving a step never asks about them; they come back from the job on the next merge.
 * "Decided" is the server's time on the field (or the audience list): a step save sends what the person
 * decided, an offer they removed included, and the server records its time even when it stays empty, so it
 * holds on the next step and in the next session too (stepPayload).
 *
 * Keys are the flat BrandFieldKey everywhere, as the API takes them.
 */

// ── Types ───────────────────────────────────────────────────────────────────────

/** A field's value as the wizard holds it: text (what the box shows), a list, or a Value philosophy choice. */
export type FieldValue = string | string[] | ValuePhilosophy | null;

export interface DraftField {
  value: FieldValue;
  /** What the server holds (as last loaded or saved). */
  saved: FieldValue;
  sources: BrandSourceTag[];
  savedSources: BrandSourceTag[];
  aiSuggested: boolean;
  savedAiSuggested: boolean;
  /** The person changed it since it was loaded, saved or discarded. */
  touched: boolean;
  /** Set while the value came from that import job and is untouched: the job may still change it. */
  importJobId: string | null;
  /** When the server last wrote this field (`provenance[key].updatedAt`): "the person decided" (§2.14). */
  decidedAt: string | null;
}

export interface DraftAudience {
  /** The card's identity in the draft and its React key: the audience's id, or a new card's own key. */
  key: string;
  /** The saved audience's id; null for a card not saved yet. */
  id: string | null;
  fields: Record<AudienceFieldKey, DraftField>;
  /** Set on a card an import added, while it is untouched. */
  importJobId: string | null;
  /** Which of that job's audiences it holds. */
  importIndex: number | null;
}

export type CompanyVoiceKey = 'coreValues' | 'mission' | 'vision';
export const COMPANY_VOICE_KEYS: readonly CompanyVoiceKey[] = ['coreValues', 'mission', 'vision'];

export interface CopySourceRef {
  tenantId: string;
  name: string;
}

/** What Undo puts back after Copy from another workspace (step 2): the three fields as they were. */
export interface CopyUndo {
  from: CopySourceRef;
  fields: Record<CompanyVoiceKey, DraftField>;
}

export interface BrandDraft {
  tenantId: string;
  /** The profile version the next save sends as `expectedVersion`. */
  version: number;
  fields: Record<BrandFieldKey, DraftField>;
  /** In display order: a new card first, imported cards last. */
  audiences: DraftAudience[];
  savedAudiences: DraftAudience[];
  /** The person added, removed or edited an audience since step 7 was loaded, saved or discarded. */
  audiencesTouched: boolean;
  /** When the audience list was last saved (`audiencesUpdatedAt`). */
  audiencesDecidedAt: string | null;
  /** Step 1's choice; imports merge only while it is UPLOAD. */
  startMode: BrandOnboardingStartMode;
  /** The import job whose offers the draft holds. */
  mergedJobId: string | null;
  copyUndo: CopyUndo | null;
  /** Identities of imported audiences that did not fit (the limit of 10). */
  importOverflow: string[];
  /** The next "Add audience" card is `new-{n}`. */
  nextAudienceKey: number;
}

/** Where a step save leaves Continue Brand Setup: a step, this step (Save and exit), or finished (step 8). */
export type StepTarget = BrandWizardStep | 'stay' | 'finish';

export type DraftAction =
  | { type: 'load'; view: BrandProfileView }
  | { type: 'edit'; key: BrandFieldKey; value: FieldValue }
  | {
      type: 'editAudience';
      key: string;
      field: AudienceFieldKey;
      value: string;
      /** A value that is not the person's own (a re-read's proposal): its tags, in place of Manual Entry. */
      sources?: BrandSourceTag[];
    }
  | { type: 'addAudience' }
  | { type: 'removeAudience'; key: string }
  | { type: 'setStartMode'; mode: BrandOnboardingStartMode }
  | { type: 'applyImport'; job: BrandImportJobView }
  | { type: 'clearImportDrafts' }
  | { type: 'copyCompanyVoice'; from: CopySourceRef; values: Record<CompanyVoiceKey, string | null> }
  | { type: 'undoCopy' }
  | { type: 'dropCopyUndo' }
  | { type: 'discardStep'; step: BrandWizardStep }
  | { type: 'stepSaved'; step: BrandWizardStep; view: BrandProfileView }
  /** Release 4's extension point (suggestions, re-read): set values with their provenance. */
  | {
      type: 'setFields';
      changes: Array<{
        key: BrandFieldKey;
        value: FieldValue;
        sources?: BrandSourceTag[];
        aiSuggested?: boolean;
        touched: boolean;
      }>;
    };

export type StepCheck = { ok: true } | { ok: false; firstInvalid: string };

// ── Ids the wizard's controls carry (a failed check names one to focus) ────────

export function wizardFieldId(key: BrandFieldKey): string {
  return `brand-setup-${key}`;
}

export function audienceFieldId(cardKey: string, field: AudienceFieldKey): string {
  return `brand-setup-audience-${cardKey}-${field}`;
}

/** Step 7's "Add audience" button. */
export const AUDIENCES_ADD_ID = 'brand-setup-add-audience';

// ── Values ──────────────────────────────────────────────────────────────────────

/** null, blank text, or an empty list. */
export function isEmptyValue(value: FieldValue | undefined): boolean {
  if (value === null || value === undefined) return true;
  if (Array.isArray(value)) return value.length === 0;
  return value.trim() === '';
}

/**
 * The same value as the server would store it: text compared as stored (trimmed, line breaks as LF,
 * blank as none), lists item by item in order.
 */
export function sameValue(a: FieldValue, b: FieldValue): boolean {
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b)) return isEmptyValue(a) && isEmptyValue(b);
    return a.length === b.length && a.every((item, i) => item === b[i]);
  }
  return normalizeBrandText(a) === normalizeBrandText(b);
}

const copyValue = (value: FieldValue): FieldValue => (Array.isArray(value) ? [...value] : value);
const textOf = (value: FieldValue): string => (typeof value === 'string' ? value : '');
const isListKind = (kind: BrandFieldKind) => kind === 'tags' || kind === 'guidelines';

/** An import value of the right shape for the field (a list for a list, text otherwise). */
function fitsKind(kind: BrandFieldKind | 'audience', value: unknown): value is FieldValue {
  if (kind !== 'audience' && isListKind(kind)) return Array.isArray(value) && value.every((v) => typeof v === 'string');
  return typeof value === 'string';
}

/** At or after: the person saved this field (even empty) once the import run had begun. */
function decidedSince(decidedAt: string | null, runStartedAt: string): boolean {
  if (decidedAt === null) return false;
  const decided = Date.parse(decidedAt);
  const started = Date.parse(runStartedAt);
  if (Number.isNaN(decided) || Number.isNaN(started)) return decidedAt >= runStartedAt;
  return decided >= started;
}

/** An audience's identity for matching: lower case, letters and digits only ("Rescue Meds" = "rescuemeds"). */
export function normIdentity(identity: string): string {
  return identity.toLocaleLowerCase().replace(/[^\p{L}\p{N}]+/gu, '');
}

// ── One field ───────────────────────────────────────────────────────────────────

function storedField(stored: FieldValue | undefined, provenance: FieldProvenanceView | undefined): DraftField {
  const value = stored ?? null;
  const empty = isEmptyValue(value);
  // A field with no content has no tags (spec:109), whatever the stored entry says.
  const sources = empty ? [] : canonicalSources(provenance?.sources ?? []);
  const aiSuggested = !empty && (provenance?.aiSuggested ?? false);
  return {
    value: copyValue(value),
    saved: copyValue(value),
    sources,
    savedSources: [...sources],
    aiSuggested,
    savedAiSuggested: aiSuggested,
    touched: false,
    importJobId: null,
    decidedAt: provenance?.updatedAt ?? null,
  };
}

const emptyField = (): DraftField => storedField(null, undefined);

function resetToSaved(f: DraftField): DraftField {
  return {
    ...f,
    value: copyValue(f.saved),
    sources: [...f.savedSources],
    aiSuggested: f.savedAiSuggested,
    touched: false,
    importJobId: null,
  };
}

/**
 * The person changed the value (spec:106-109): Manual Entry joins the tags it had; an empty field has no
 * tags; back to exactly the saved value, it has the saved tags again. Either way it is the person's now:
 * no import fills it again this session (a cleared field stays cleared).
 */
function editField(f: DraftField, value: FieldValue): DraftField {
  let sources: BrandSourceTag[];
  let aiSuggested: boolean;
  if (sameValue(value, f.saved)) {
    sources = [...f.savedSources];
    aiSuggested = f.savedAiSuggested;
  } else if (isEmptyValue(value)) {
    sources = [];
    aiSuggested = false;
  } else {
    sources = canonicalSources([...f.sources, 'manualEntry']);
    aiSuggested = f.aiSuggested;
  }
  return { ...f, value: copyValue(value), sources, aiSuggested, touched: true, importJobId: null };
}

/**
 * An import's offer for one field. It fills the field only when the field is untouched, holds no
 * content of its own (saved or copied) and was not decided since the run began; a later revision of the
 * same job may update its own fill.
 */
function fillField(f: DraftField, offer: BrandDraftValue<unknown>, kind: BrandFieldKind | 'audience', job: BrandImportJobView): DraftField {
  if (f.touched) return f;
  if (f.importJobId === null && !isEmptyValue(f.value)) return f;
  if (decidedSince(f.decidedAt, job.runStartedAt)) return f;
  if (!fitsKind(kind, offer.value) || isEmptyValue(offer.value)) return forgetFill(f, job.id);
  const sources = canonicalSources(offer.sources ?? []);
  return {
    ...f,
    value: copyValue(offer.value),
    sources: sources.length > 0 ? sources : ['brandDocument'],
    aiSuggested: false,
    importJobId: job.id,
  };
}

/** A fill from `jobId` that the job no longer offers goes back to the saved value. */
const forgetFill = (f: DraftField, jobId: string): DraftField => (f.importJobId === jobId ? resetToSaved(f) : f);

function mapFields<K extends string>(fields: Record<K, DraftField>, fn: (f: DraftField, key: K) => DraftField): Record<K, DraftField> {
  const out = { ...fields };
  for (const key of Object.keys(fields) as K[]) out[key] = fn(fields[key], key);
  return out;
}

// ── Audiences ───────────────────────────────────────────────────────────────────

function emptyAudienceFields(): Record<AudienceFieldKey, DraftField> {
  const fields = {} as Record<AudienceFieldKey, DraftField>;
  for (const key of AUDIENCE_FIELD_KEYS) fields[key] = emptyField();
  return fields;
}

function storedAudience(a: BrandAudienceView): DraftAudience {
  const fields = {} as Record<AudienceFieldKey, DraftField>;
  for (const key of AUDIENCE_FIELD_KEYS) fields[key] = storedField(a[key], a.provenance[key]);
  return { key: a.id, id: a.id, fields, importJobId: null, importIndex: null };
}

function storedAudiences(view: BrandProfileView): DraftAudience[] {
  return [...view.audiences].sort((a, b) => a.order - b.order).map(storedAudience);
}

const isBlankCard = (card: DraftAudience) => AUDIENCE_FIELD_KEYS.every((key) => isEmptyValue(card.fields[key].value));

/** The cards a step 7 save sends: every saved audience, and the new ones with something in them. */
function cardsToSave(cards: readonly DraftAudience[]): DraftAudience[] {
  return cards.filter((card) => card.id !== null || !isBlankCard(card));
}

/** A field of this card is over its length limit (the card stays open so the error shows). */
export function audienceHasError(card: DraftAudience): boolean {
  return AUDIENCE_FIELD_KEYS.some(
    (key) => textLimitError(textOf(card.fields[key].value), AUDIENCE_FIELD_DEFINITIONS[key].maxLength) !== null,
  );
}

type ImportedAudience = BrandImportDraft['audiences'][number];

function mergeAudienceFields(
  fields: Record<AudienceFieldKey, DraftField>,
  offered: ImportedAudience['fields'],
  job: BrandImportJobView,
): Record<AudienceFieldKey, DraftField> {
  return mapFields(fields, (f, key) => {
    const offer = offered[key];
    return offer ? fillField(f, offer, 'audience', job) : forgetFill(f, job.id);
  });
}

/** The audience merge runs only while the person has not touched the list or saved it since the run began. */
const audienceMergeAllowed = (s: BrandDraft, job: BrandImportJobView) =>
  !s.audiencesTouched && !decidedSince(s.audiencesDecidedAt, job.runStartedAt);

/**
 * Imported audiences, in document order: one whose identity matches a card fills that card's empty
 * fields; another becomes a new collapsed card after the others, up to 10; past that it is listed as not
 * imported. Cards this job added before are found again by their place in the job, so a later revision
 * updates them instead of adding them twice.
 */
function mergeAudiences(s: BrandDraft, job: BrandImportJobView, imported: readonly ImportedAudience[]): BrandDraft {
  const cards = [...s.audiences];
  const used = new Set<string>();
  const overflow: string[] = [];
  imported.forEach((audience, index) => {
    const offeredIdentity = audience.fields.identity?.value;
    const identity = typeof offeredIdentity === 'string' ? offeredIdentity.trim() : '';
    const wanted = normIdentity(identity);
    let at = cards.findIndex((c) => !used.has(c.key) && c.importJobId === job.id && c.importIndex === index);
    if (at < 0 && wanted !== '') {
      at = cards.findIndex((c) => !used.has(c.key) && normIdentity(textOf(c.fields.identity.value)) === wanted);
    }
    if (at >= 0) {
      const card = cards[at];
      used.add(card.key);
      cards[at] = { ...card, fields: mergeAudienceFields(card.fields, audience.fields, job) };
      return;
    }
    if (cards.length >= BRAND_LIMITS.audiences) {
      overflow.push(identity || `Audience ${index + 1}`);
      return;
    }
    const card: DraftAudience = {
      key: `imported-${job.id}-${index}`,
      id: null,
      fields: mergeAudienceFields(emptyAudienceFields(), audience.fields, job),
      importJobId: job.id,
      importIndex: index,
    };
    used.add(card.key);
    cards.push(card);
  });
  // A card this job added that its draft no longer has.
  const audiences = cards.filter((c) => c.importJobId !== job.id || used.has(c.key));
  return { ...s, audiences, importOverflow: overflow };
}

/** Drops every import offer in the draft: untouched fills go back to saved, imported cards go. */
function clearImportDrafts(s: BrandDraft): BrandDraft {
  const unfill = (f: DraftField) => (f.importJobId !== null ? resetToSaved(f) : f);
  return {
    ...s,
    fields: mapFields(s.fields, unfill),
    audiences: s.audiences
      .filter((card) => card.importJobId === null)
      .map((card) => ({ ...card, fields: mapFields(card.fields, unfill) })),
    mergedJobId: null,
    importOverflow: [],
  };
}

/**
 * Merges an import job's draft (web.md §5.3). A no-op unless step 1's choice is Upload and prefill. A
 * job other than the one merged before (a replaced document) first drops the old job's untouched fills,
 * even while the new one has nothing yet.
 */
function applyImport(s: BrandDraft, job: BrandImportJobView): BrandDraft {
  if (s.startMode !== 'UPLOAD') return s;
  let next = s;
  if (job.id !== s.mergedJobId) {
    const otherJob = (f: DraftField) => (f.importJobId !== null && f.importJobId !== job.id ? resetToSaved(f) : f);
    next = { ...next, fields: mapFields(next.fields, otherJob), mergedJobId: job.id, importOverflow: [] };
    if (audienceMergeAllowed(next, job)) {
      next = {
        ...next,
        audiences: next.audiences
          .filter((card) => card.importJobId === null || card.importJobId === job.id)
          .map((card) => ({ ...card, fields: mapFields(card.fields, otherJob) })),
      };
    }
  }
  const draft = job.draft;
  if (!draft) return next;

  const fields = { ...next.fields };
  for (const key of BRAND_FIELD_KEYS) {
    const offer: BrandDraftValue<unknown> | undefined = draft.fields[key];
    fields[key] = offer ? fillField(fields[key], offer, BRAND_FIELD_DEFINITIONS[key].kind, job) : forgetFill(fields[key], job.id);
  }
  next = { ...next, fields };
  return audienceMergeAllowed(next, job) ? mergeAudiences(next, job, draft.audiences) : next;
}

// ── Steps ───────────────────────────────────────────────────────────────────────

/** The brand fields a step shows and saves (step 1 and step 7: none). */
export function stepFieldKeys(step: BrandWizardStep): readonly BrandFieldKey[] {
  return BRAND_WIZARD_STEPS.find((s) => s.step === step)?.fields ?? [];
}

/** A value as far as the person is concerned: an untouched import fill still counts as the saved value. */
const ownValue = (f: DraftField): FieldValue => (f.importJobId !== null ? f.saved : f.value);

const sameOwnCard = (a: DraftAudience, b: DraftAudience) =>
  a.id === b.id && AUDIENCE_FIELD_KEYS.every((key) => sameValue(ownValue(a.fields[key]), b.fields[key].value));

/**
 * Whether leaving the step would lose something the person did (web.md §4.3): a field they changed to
 * something other than its saved value, or (step 7) their audience list differing from the saved one.
 * Untouched import fills and cards do not count: they stay in the job and come back. Neither does a new
 * card left blank, which a save would not send.
 */
export function stepHasUserChanges(s: BrandDraft, step: BrandWizardStep): boolean {
  if (step === 7) {
    if (!s.audiencesTouched) return false;
    const own = s.audiences.filter(
      (card) =>
        card.importJobId === null &&
        (card.id !== null || !AUDIENCE_FIELD_KEYS.every((key) => isEmptyValue(ownValue(card.fields[key])))),
    );
    return own.length !== s.savedAudiences.length || own.some((card, i) => !sameOwnCard(card, s.savedAudiences[i]));
  }
  return stepFieldKeys(step).some((key) => {
    const f = s.fields[key];
    return f.touched && !sameValue(f.value, f.saved);
  });
}

/** Lengths and counts the step can save; else the id of the first field to fix. */
export function stepIsValid(s: BrandDraft, step: BrandWizardStep): StepCheck {
  for (const key of stepFieldKeys(step)) {
    const def = BRAND_FIELD_DEFINITIONS[key];
    if ((def.kind === 'text' || def.kind === 'textarea') && textLimitError(textOf(s.fields[key].value), def.maxLength)) {
      return { ok: false, firstInvalid: wizardFieldId(key) };
    }
  }
  if (step === 7) {
    for (const card of s.audiences) {
      for (const key of AUDIENCE_FIELD_KEYS) {
        if (textLimitError(textOf(card.fields[key].value), AUDIENCE_FIELD_DEFINITIONS[key].maxLength)) {
          return { ok: false, firstInvalid: audienceFieldId(card.key, key) };
        }
      }
    }
    if (cardsToSave(s.audiences).length > BRAND_LIMITS.audiences) return { ok: false, firstInvalid: AUDIENCES_ADD_ID };
  }
  return { ok: true };
}

/** A value as the step save sends it: text as stored (blank is null), a list as it is, a choice or null. */
function payloadValue(kind: BrandFieldKind | 'audience', value: FieldValue): FieldValue {
  if (kind !== 'audience' && isListKind(kind)) return Array.isArray(value) ? [...value] : [];
  if (kind === 'valuePhilosophy') return typeof value === 'string' && value !== '' ? value : null;
  return typeof value === 'string' ? normalizeBrandText(value) : null;
}

function fieldInput(kind: BrandFieldKind | 'audience', f: DraftField): BrandFieldInput<FieldValue> {
  const value = payloadValue(kind, f.value);
  const empty = isEmptyValue(value);
  return { value, sources: empty ? [] : canonicalSources(f.sources), aiSuggested: !empty && f.aiSuggested };
}

function audienceInput(card: DraftAudience): BrandAudienceInput {
  const fields: BrandAudienceInput['fields'] = {};
  for (const key of AUDIENCE_FIELD_KEYS) fields[key] = fieldInput('audience', card.fields[key]) as BrandFieldInput<string | null>;
  return card.id !== null ? { id: card.id, fields } : { fields };
}

/**
 * A field the step save sends: one with content, one the person changed (an import's offer they removed
 * included) or one holding an offer they accept by saving. A field that is empty and that nothing decided
 * is left out: the server records the time of every field sent, empty ones included, and the import merge
 * reads that time as "decided since the run began", so a field left empty only because the import had not
 * delivered yet stays open to it (final-design §2.14).
 */
const sendsField = (f: DraftField): boolean => f.touched || f.importJobId !== null || !isEmptyValue(f.value);

/** Step 7's list goes in its save once the person changed it or it holds an import's offer (a card, or a fill). */
const sendsAudiences = (s: BrandDraft): boolean =>
  s.audiencesTouched ||
  s.audiences.some((card) => card.importJobId !== null || AUDIENCE_FIELD_KEYS.some((key) => card.fields[key].importJobId !== null));

/**
 * The body of one step's save (final-design §2.4.2, §2.14): `resumeAt` is the step Continue reopens at
 * (`'stay'` = this step, `'finish'` = step 8 with `finish`), and the body carries ONLY what the step
 * saves: step 1 its start choice; steps 2-6 and 8 their own fields with their sources (sendsField); step 7
 * the whole ordered audience list, without new cards that are still blank (sendsAudiences). The server
 * refuses anything else (BRAND_STEP_MISMATCH).
 */
export function stepPayload(s: BrandDraft, step: BrandWizardStep, goTo: StepTarget): OnboardingStepSaveRequest {
  const resumeAt: BrandWizardStep = goTo === 'stay' ? step : goTo === 'finish' ? BRAND_WIZARD_STEP_COUNT : goTo;
  const payload: OnboardingStepSaveRequest = { expectedVersion: s.version, resumeAt };
  if (goTo === 'finish' && step === BRAND_WIZARD_STEP_COUNT) payload.finish = true;
  if (step === 1) {
    payload.startMode = s.startMode;
    return payload;
  }
  if (step === 7) {
    if (!sendsAudiences(s)) return payload;
    const cards = cardsToSave(s.audiences);
    payload.audiences = cards.map(audienceInput);
    const jobId = cards
      .flatMap((card) => [card.importJobId, ...AUDIENCE_FIELD_KEYS.map((key) => card.fields[key].importJobId)])
      .find((id): id is string => id !== null);
    if (jobId) payload.importJobId = jobId;
    return payload;
  }
  const fields: Partial<Record<BrandFieldKey, BrandFieldInput<FieldValue>>> = {};
  for (const key of stepFieldKeys(step)) {
    if (sendsField(s.fields[key])) fields[key] = fieldInput(BRAND_FIELD_DEFINITIONS[key].kind, s.fields[key]);
  }
  payload.fields = fields as BrandFieldsInput;
  if (step === 2 && s.copyUndo) payload.copiedFromTenantId = s.copyUndo.from.tenantId;
  const jobId = stepFieldKeys(step)
    .map((key) => s.fields[key].importJobId)
    .find((id): id is string => id !== null);
  if (jobId) payload.importJobId = jobId;
  return payload;
}

// ── The draft ───────────────────────────────────────────────────────────────────

/** The draft of a freshly loaded profile: every value as saved, nothing touched, no import merged. */
export function initialBrandDraft(view: BrandProfileView): BrandDraft {
  const fields = {} as Record<BrandFieldKey, DraftField>;
  for (const key of BRAND_FIELD_KEYS) fields[key] = storedField(view.fields[key], view.provenance[key]);
  const audiences = storedAudiences(view);
  return {
    tenantId: view.tenantId,
    version: view.version,
    fields,
    audiences,
    savedAudiences: audiences,
    audiencesTouched: false,
    audiencesDecidedAt: view.audiencesUpdatedAt,
    // Upload and prefill is chosen by default (spec:85).
    startMode: view.onboarding.startMode ?? 'UPLOAD',
    mergedJobId: null,
    copyUndo: null,
    importOverflow: [],
    nextAudienceKey: 1,
  };
}

export function brandDraftReducer(s: BrandDraft, a: DraftAction): BrandDraft {
  switch (a.type) {
    case 'load':
      // A reload after a version conflict: the server's values; the step's unsaved changes are gone.
      return { ...initialBrandDraft(a.view), startMode: s.startMode, nextAudienceKey: s.nextAudienceKey };

    case 'edit':
      return { ...s, fields: { ...s.fields, [a.key]: editField(s.fields[a.key], a.value) } };

    case 'editAudience': {
      const edit = (f: DraftField): DraftField => {
        const edited = editField(f, a.value);
        return a.sources ? { ...edited, sources: canonicalSources(a.sources) } : edited;
      };
      return {
        ...s,
        audiences: s.audiences.map((card) =>
          card.key !== a.key
            ? card
            : {
                ...card,
                // The person's card now: no import adds to it or takes it away.
                importJobId: null,
                importIndex: null,
                fields: { ...card.fields, [a.field]: edit(card.fields[a.field]) },
              },
        ),
        audiencesTouched: true,
      };
    }

    case 'addAudience': {
      if (s.audiences.length >= BRAND_LIMITS.audiences) return s;
      const card: DraftAudience = {
        key: `new-${s.nextAudienceKey}`,
        id: null,
        fields: emptyAudienceFields(),
        importJobId: null,
        importIndex: null,
      };
      // Above the existing ones (spec:267).
      return { ...s, audiences: [card, ...s.audiences], nextAudienceKey: s.nextAudienceKey + 1, audiencesTouched: true };
    }

    case 'removeAudience':
      return { ...s, audiences: s.audiences.filter((card) => card.key !== a.key), audiencesTouched: true };

    case 'setStartMode':
      // Fill in manually clears what an import filled in; saved values stay (E29).
      return a.mode === 'MANUAL' ? { ...clearImportDrafts(s), startMode: 'MANUAL' } : { ...s, startMode: 'UPLOAD' };

    case 'applyImport':
      return applyImport(s, a.job);

    case 'clearImportDrafts':
      return clearImportDrafts(s);

    case 'copyCompanyVoice': {
      // A snapshot, not a link (spec:153): every one of the three takes the copied text, empty included,
      // tagged Manual Entry.
      const snapshot = { coreValues: s.fields.coreValues, mission: s.fields.mission, vision: s.fields.vision };
      const fields = { ...s.fields };
      for (const key of COMPANY_VOICE_KEYS) {
        const value = a.values[key] ?? null;
        const empty = isEmptyValue(value);
        fields[key] = {
          ...fields[key],
          value,
          sources: empty ? [] : ['manualEntry'],
          aiSuggested: false,
          touched: true,
          importJobId: null,
        };
      }
      return { ...s, fields, copyUndo: { from: a.from, fields: snapshot } };
    }

    case 'undoCopy':
      if (!s.copyUndo) return s;
      return { ...s, fields: { ...s.fields, ...s.copyUndo.fields }, copyUndo: null };

    case 'dropCopyUndo':
      return s.copyUndo ? { ...s, copyUndo: null } : s;

    case 'discardStep': {
      const fields = { ...s.fields };
      for (const key of stepFieldKeys(a.step)) fields[key] = resetToSaved(fields[key]);
      let next: BrandDraft = { ...s, fields };
      if (a.step === 7) next = { ...next, audiences: s.savedAudiences, audiencesTouched: false, importOverflow: [] };
      if (a.step === 2) next = { ...next, copyUndo: null };
      return next;
    }

    case 'stepSaved': {
      // Exactly that step reloads from the server's answer; the other steps keep their drafts.
      const view = a.view;
      const fields = { ...s.fields };
      for (const key of stepFieldKeys(a.step)) fields[key] = storedField(view.fields[key], view.provenance[key]);
      let next: BrandDraft = { ...s, version: view.version, fields };
      if (a.step === 7) {
        const audiences = storedAudiences(view);
        next = {
          ...next,
          audiences,
          savedAudiences: audiences,
          audiencesTouched: false,
          audiencesDecidedAt: view.audiencesUpdatedAt,
        };
      }
      if (a.step === 2) next = { ...next, copyUndo: null };
      if (a.step === 1 && view.onboarding.startMode) next = { ...next, startMode: view.onboarding.startMode };
      return next;
    }

    case 'setFields': {
      const fields = { ...s.fields };
      for (const change of a.changes) {
        const f = fields[change.key];
        const empty = isEmptyValue(change.value);
        fields[change.key] = {
          ...f,
          value: copyValue(change.value),
          sources: empty ? [] : canonicalSources(change.sources ?? f.sources),
          aiSuggested: !empty && (change.aiSuggested ?? f.aiSuggested),
          touched: change.touched,
          importJobId: null,
        };
      }
      return { ...s, fields };
    }
  }
}
